import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";

import { catchError, delay, map, switchMap, tap } from "rxjs/operators";
import { AuthActions } from "@store/actions/auth/auth.actions";
import { of } from "rxjs";
import { UserAdminActions } from "@store/actions/user-admin/user-admin.actions";
import { UserAdminService } from "@services/user-admin.service";
import { DropdownsService } from "@services/dropdowns.service";
import { Store } from "@ngrx/store";
import { concatLatestFrom } from "@ngrx/operators";
import {
  selectOrgFilterSearch,
  selectRoleFilterSearch,
} from "@store/selectors/user-admin/user-admin.selector";
import { routerRequestActionTo } from "@store/actions/function-actions/router-request.action";
import { ClinicianDetailsActions } from "@store/actions/clinician-details/clinician-details.actions";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { AddOrganizationActions } from "@store/actions/add-organization/add-organization.actions";
import { UserActions } from "@store/actions/user/user.actions";

@Injectable()
export class UserAdminEffects {
  getUsersOnNavigate$ = createEffect(() => {
    return this.actions$.pipe(
      routerRequestActionTo("/useradmin"),
      switchMap(() => {
        return this.userAdminService.getUsers().pipe(
          map((data) => UserAdminActions.getUsersSuccess({ users: data })),
          catchError((error) => of(UserAdminActions.getUsersFailure({ error })))
        );
      })
    );
  });

  addUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserAdminActions.addUser),
      switchMap((action) => {
        return this.userAdminService.addUser(action.user).pipe(
          map((data) => UserAdminActions.addUserSuccess({ user: data })),
          catchError((error) => of(UserAdminActions.addUserFailure({ error })))
        );
      })
    );
  });

updateUser$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(UserAdminActions.updateUser),
    switchMap((action) => {
      return this.userAdminService.updateUser(action.user).pipe(
        map((data) => {
          if (action.isLoginUpdate) {
            return UserActions.getUserOnLoginSuccess({ user: data });
          }
          return UserAdminActions.updateUserSuccess({ user: data });
        }),
        catchError((error) => {
          return of(UserAdminActions.updateUserFailure({ error }));
        })
      );
    })
  );
});

  deleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserAdminActions.deleteUser),
      switchMap((action) => {
        return this.userAdminService.deleteUser(action.user.id).pipe(
          map((data) =>
            UserAdminActions.deleteUserSuccess({
              userId: action.user.id,
            })
          ),
          catchError((error) =>
            of(UserAdminActions.deleteUserFailure({ error }))
          )
        );
      })
    );
  });

  getRolesDropdown$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserAdminActions.pageLoaded,
        ClinicianDetailsActions.clinicianDetailsComponentLoaded,
        UserAdminActions.roleSearchChanged
      ),
      concatLatestFrom(() => [this.store.select(selectRoleFilterSearch)]),
      switchMap(([, data]) => {
        return this.dropdownsService.getUserRoles({ data }).pipe(
          map((data) => UserAdminActions.getRolesSuccess({ roles: data })),
          catchError((error) => of(UserAdminActions.getRolesFailure({ error })))
        );
      })
    );
  });

  loadOrgsOnLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.getUserOnLoginSuccess),
      switchMap(({ user }) =>
        this.userAdminService.getOrganizations("").pipe(
          switchMap((orgs) =>
            of(
              UserAdminActions.getOrganizationsSuccess({ orgs }),
              UserActions.loginOrgsLoaded({ user, orgs }),
            )
          ),
          catchError((error) => {
            console.error("[UserAdminEffects] Failed to load org list on login:", error);
            return of(
              AuthActions.loginError({
                error: "Unable to load organization information. Please try again later.",
              }),
            );
          }),
        )
      ),
    );
  });

  getOrgs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserAdminActions.pageLoaded,
        ClinicianDetailsActions.clinicianDetailsComponentLoaded,
        UserAdminActions.orgSearchChanged,
        IssueTrackerActions.getTickets,
        AddOrganizationActions.createNewOrganizationSuccess
      ),
      concatLatestFrom(() => [this.store.select(selectOrgFilterSearch)]),
      switchMap(([, data]) => {
        return this.userAdminService.getOrganizations(data).pipe(
          map((data) =>
            UserAdminActions.getOrganizationsSuccess({ orgs: data })
          ),
          catchError((error) =>
            of(UserAdminActions.getOrganizationsFailure({ error }))
          )
        );
      })
    );
  });

  resetPasswordPressed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UserAdminActions.resetUserPasswordPressed),
        tap((action) => {
          const dialogRef = this.dialog.open(ConfirmationModalComponent, {
            data: {
              title: "Reset Password",
              content: `Are you sure you want to reset this user's password?`,
              confirmText: "Reset",
              cancelText: "Cancel",
            },
          });

          dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
              this.store.dispatch(UserAdminActions.resetUserPassword(action));
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserAdminActions.resetUserPassword),
      switchMap((action) => {
        return this.userAdminService.resetPassword(action.userId).pipe(
          map((data) => {
            return UserAdminActions.resetUserPasswordSuccess();
          }),
          catchError((error) => {
            return of(UserAdminActions.resetUserPasswordFailure({ error }));
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private dropdownsService: DropdownsService,
    private userAdminService: UserAdminService,
    private store: Store,
    private dialog: MatDialog
  ) {}
}
